import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { UserRoles, IGetRestaurantsShort } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getRestaurantsByCategoryShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantMultipleSelect.module.scss';

interface IProps {
	setSelectedRestaurants: (value: string[]) => void;
	selectedRestaurants: string[];
	categoryId?: string;
}

export const RestaurantMultipleSelect: React.FC<IProps> = React.memo(
	({ setSelectedRestaurants, selectedRestaurants, categoryId }) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);
		const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, string>(
			categoryId ? getRestaurantsByCategoryShortInfo : null,
			categoryId || '',
		);

		useEffect(() => {
			refetch();
		}, [categoryId]);

		const handleRemove = useCallback(
			(id: string) => {
				const selectedItems = selectedRestaurants.filter((el) => el !== id);
				setSelectedRestaurants(selectedItems);
			},
			[setSelectedRestaurants, selectedRestaurants],
		);

		const restaurants = useMemo(() => {
			return response?.data.restaurants || [];
		}, [response]);

		const restaurantsList = useMemo(() => {
			return selectedRestaurants.map((el) => {
				const label = restaurants.find((item) => item.id === el)?.name || '';
				const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
				return (
					<SelectedItem
						key={el}
						label={label}
						onDelete={
							!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)
						}
					/>
				);
			});
		}, [handleRemove, selectedRestaurants, restaurants, loggedUser, hasAccess]);

		return (
			<div className={style.container} data-test="RestaurantField">
				<MultipleSelect
					data={restaurants}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					setSelectedRestaurants={setSelectedRestaurants}
					selectedRestaurants={selectedRestaurants}
				/>
				<div className={style.groupsList}>{restaurantsList}</div>
			</div>
		);
	},
);
